/**
 * A signed-in session that holds no admin role.
 *
 * `adminMiddleware` (apps/backend/src/modules/admin/admin.middleware.ts) answers
 * 403 with exactly this message when its role lookup succeeded, found no admin
 * role, and the user is not on the env allowlist. A failed lookup answers 503
 * instead. Change the two together; admin.middleware.test.ts pins that side.
 *
 * The console's session lives in its own cookies, which only the admin
 * console's sign-in sets, and that sign-in requires an admin role. So the way
 * an operator gets here is losing every admin role while signed in. Signing in
 * to the Finishd app in the same browser no longer replaces this session
 * (#457).
 */
export const ADMIN_ACCESS_REQUIRED_MESSAGE = "Admin access required";

/** The `reason` the login page reads to explain the sign-out. */
export const NOT_ADMIN_REASON = "not-admin";
export const NOT_ADMIN_LOGIN_PATH = `/login?reason=${NOT_ADMIN_REASON}`;

/** The sign-in pages, where the operator is already being asked to sign in. */
const SIGN_IN_PATHS = new Set(["/login", "/verify-otp"]);

export function isNonAdminSessionError(
	status: number,
	message: string,
): boolean {
	return status === 403 && message === ADMIN_ACCESS_REQUIRED_MESSAGE;
}

/**
 * Sends the operator to the login notice with a full page load. The login
 * page clears the stored session when it opens with this reason; doing it
 * here instead would fire the auth-change event, and the protected route's
 * own redirect to a bare /login could race this one and drop the notice.
 *
 * On the sign-in pages it does nothing, so the page's own error handling
 * shows the message and a failing request there cannot reload it in a loop.
 */
export function endNonAdminSession(): void {
	if (SIGN_IN_PATHS.has(window.location.pathname)) return;
	window.location.assign(NOT_ADMIN_LOGIN_PATH);
}
